declare global {
  interface Window {
    turnstile: any;
  }
}
'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  MenuItem,
  CircularProgress,
  Chip,
  Autocomplete,
  Stack,
  alpha,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import { countries } from '../utils/countries'; // optional: ISO country list
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const collectionAgencies = [
  'PRS', 'PPL', 'MCPS', 'ASCAP', 'BMI', 'SESAC', 'SOCAN', 'APRA AMCOS', 'IMRO',
  'GEMA', 'SACEM', 'BUMA/STEMRA', 'STIM', 'TONO', 'APRA', 'Other'
];

// Custom styled components
const StyledTextField = (props: any) => (
  <TextField
    {...props}
    InputLabelProps={{
      style: { fontFamily: '"Poppins", sans-serif', fontWeight: 500 },
      shrink: undefined,
      ...props.InputLabelProps
    }}
    InputProps={{
      style: { fontFamily: '"Poppins", sans-serif' },
      ...props.InputProps
    }}
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        height: props.multiline ? 'auto' : '42px',
        '&:hover fieldset': {
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.6),
        },
        '&.Mui-focused fieldset': {
          borderWidth: '2px',
        }
      },
      '& .MuiInputBase-input': {
        padding: props.multiline ? '8px 12px' : '6px 12px',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.85rem',
        transform: 'translate(12px, 12px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(12px, -9px) scale(0.85)',
        }
      },
      '& .MuiOutlinedInput-notchedOutline': {
        paddingLeft: '8px',
      },
      ...props.sx
    }}
    margin="dense"
    size="small"
    variant="outlined"
  />
);

// Enhanced dropdown style
const StyledSelect = (props: any) => {
  const { label, options, ...otherProps } = props;
  
  return (
    <FormControl 
      fullWidth 
      size="small" 
      sx={{ 
        margin: '4px 0',
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          height: '42px',
          fontFamily: '"Poppins", sans-serif',
          '&:hover fieldset': {
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.6),
          },
          '&.Mui-focused fieldset': {
            borderWidth: '2px',
          },
          // Make the dropdown icon more visible
          '& .MuiSvgIcon-root': {
            color: '#000000', // Black for visibility
            fontSize: '24px',
            right: '7px',
          },
          // Ensure the entire field is clickable
          cursor: 'pointer',
          '& .MuiSelect-select': {
            cursor: 'pointer',
          }
        },
        '& .MuiSelect-select': {
          padding: '6px 12px',
          paddingRight: '32px !important',
        },
        '& .MuiInputLabel-root': {
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 500,
          fontSize: '0.85rem',
          transform: 'translate(12px, 12px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(12px, -9px) scale(0.85)',
          }
        },
        ...otherProps.sx
      }}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        {...otherProps}
        label={label}
        // Using the default IconComponent
        // Improving menu appearance
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              fontFamily: '"Poppins", sans-serif',
            },
          },
          // This makes the entire dropdown component clickable to open the menu
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          }
        }}
      >
        {options.map((option: any) => {
          // Handle different formats for country vs other options
          if (typeof option === 'object' && option.code && option.name) {
            return (
              <MenuItem 
                key={option.code} 
                value={option.name} 
                sx={{ fontFamily: '"Poppins", sans-serif' }}
              >
                {option.name}
              </MenuItem>
            );
          }
          return (
            <MenuItem 
              key={option} 
              value={option} 
              sx={{ fontFamily: '"Poppins", sans-serif' }}
            >
              {option}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const StyledAutocomplete = (props: any) => {
  const { label, ...otherProps } = props;
  
  return (
    <Autocomplete
      {...otherProps}
      ChipProps={{
        sx: {
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 500,
          borderRadius: '6px',
          height: '24px',
          '& .MuiChip-label': {
            fontSize: '0.75rem',
            padding: '0 6px',
          },
          color: '#000000' // Ensure chip text is black
        }
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          minHeight: '42px',
          padding: '3px 8px !important',
        },
        '& .MuiChip-root': {
          fontFamily: '"Poppins", sans-serif',
          margin: '2px',
          color: '#000000' // Ensure chip text is black
        },
        '& .MuiAutocomplete-endAdornment': {
          top: 'calc(50% - 12px)',
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.85rem',
          transform: 'translate(12px, 12px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(12px, -9px) scale(0.85)',
          }
        },
        ...otherProps.sx
      }}
      renderInput={(params) => (
        <StyledTextField 
          {...params} 
          label={label}
        />
      )}
    />
  );
};

const StyledFormControlLabel = (props: any) => (
  <FormControlLabel
    {...props}
    sx={{ 
      '& .MuiFormControlLabel-label': {
        fontFamily: '"Poppins", sans-serif',
        fontSize: '0.85rem',
      },
      '& .MuiCheckbox-root': {
        padding: '4px',
      },
      margin: 0,
      ...props.sx
    }}
  />
);

const JoinForm = () => {
  console.log("JoinForm RENDER START");
  const [genresList, setGenresList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  
  // Fetch genres
  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/public/genres`);
        const data = await response.json();
        setGenresList(data.map((g: { name: string }) => g.name));
      } catch (error) {
        console.error('Error fetching genres:', error);
      }
    };
    fetchGenres();
  }, []);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone_number: '',
    artist_name: '',
    dsp_link: '',
    country: '',
    genres: [] as string[],
    distributor: '',
    collection_agencies: [] as string[],
    release_plan: '',
    message: '',
    wants_exclusives: false,
    owns_rights: false,
    release_plan_albums: 0,
    release_plan_eps: 0,
    release_plan_singles: 0,
    genre_ids: [] as number[],
    has_collection_agency: false,
    social_instagram: '',
    social_tiktok: '',
    social_youtube: '',
    social_other: '',
    music_link_type: '',
    music_link_url: '',
    has_recording_agreement: false,
    record_label_name: '',
    has_publishing_agreement: false,
    publisher_name: '',
    heard_about: '',
  });

  useEffect(() => {
    console.log("TURNSTILE_DEBUG: JoinForm mounted. Waiting for Turnstile API to load...");
    let attemptInterval: ReturnType<typeof setTimeout>;

    const attemptRenderTurnstile = () => {
      if (window.turnstile && turnstileContainerRef.current && !turnstileWidgetIdRef.current) {
        console.log("TURNSTILE_DEBUG: API loaded, container ready, widget not yet rendered. Rendering widget...");
        try {
          const widgetId = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: '0x4AAAAAABfgu43iKR6cfJY3',
            callback: (token: string) => {
              console.log("TURNSTILE_DEBUG: Success callback! Token received:", token ? `[TOKEN_PRESENT - length: ${token.length}]` : "[NO_TOKEN]");
              setTurnstileToken(token);
            },
            'expired-callback': () => {
              console.log("TURNSTILE_DEBUG: Expired callback. Clearing token.");
              setTurnstileToken('');
            },
            'error-callback': (errorCode: any) => {
              console.error("TURNSTILE_DEBUG: Error callback. Error code:", errorCode);
              setTurnstileToken('');
              setError('Verification challenge failed. Please try refreshing the page.');
            },
            'unsupported-callback': () => {
              console.error("TURNSTILE_DEBUG: Unsupported callback. Browser not supported?");
              setError("Verification challenge not supported by your browser.");
            }
          });
          turnstileWidgetIdRef.current = widgetId;
          console.log("TURNSTILE_DEBUG: Widget rendered with ID:", widgetId);
          clearInterval(attemptInterval); // Stop trying once rendered
        } catch (renderError) {
          console.error("TURNSTILE_DEBUG: Error calling window.turnstile.render:", renderError);
          setError('Could not load verification challenge. Please refresh.');
        }
      } else {
        // console.log("TURNSTILE_DEBUG: Conditions not met for rendering.", {hasApi: !!window.turnstile, hasContainer: !!turnstileContainerRef.current, hasWidgetId: !!turnstileWidgetIdRef.current});
      }
    };

    attemptRenderTurnstile(); // Initial attempt
    attemptInterval = setInterval(attemptRenderTurnstile, 1000); // Retry every second

    return () => {
      console.log("TURNSTILE_DEBUG: JoinForm unmounting. Cleaning up Turnstile.");
      clearInterval(attemptInterval);
      if (window.turnstile && turnstileWidgetIdRef.current) {
        console.log("TURNSTILE_DEBUG: Removing widget:", turnstileWidgetIdRef.current);
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    console.log("TURNSTILE_DEBUG: turnstileToken state changed to:", turnstileToken ? `[TOKEN_PRESENT - length: ${turnstileToken.length}]` : "[NO_TOKEN]");
  }, [turnstileToken]);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("TURNSTILE_DEBUG: handleSubmit called.");
    setLoading(true);
    setError('');

    if (!form.owns_rights) {
      setError('You must confirm you own the rights to the music.');
      setLoading(false);
      return;
    }

    if (!turnstileToken) {
      console.log("TURNSTILE_DEBUG: Submission blocked, no Turnstile token.");
      setError('Please complete the human verification step.');
      setLoading(false);
      return;
    }
    console.log("TURNSTILE_DEBUG: Submitting with token:", turnstileToken);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/public/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...form, 
          cfTurnstileToken: turnstileToken 
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err.message || 'Something went wrong.');
      if (err.message?.includes('verification') || err.message?.includes('Turnstile')) {
        console.log("TURNSTILE_DEBUG: Verification error on submit. Clearing token & resetting widget.");
        setTurnstileToken('');
        if (window.turnstile && turnstileWidgetIdRef.current) {
            window.turnstile.reset(turnstileWidgetIdRef.current);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Typography variant="h5" sx={{ mt: 4, fontFamily: '"Poppins", sans-serif', fontWeight: 700, textAlign: 'center' }}>
        🎉 You're on the list! We'll be in touch soon.
      </Typography>
    );
  }

  const isButtonDisabled = loading || !turnstileToken;
  // console.log("TURNSTILE_DEBUG: Button disabled state:", isButtonDisabled, "(loading:", loading, ", !turnstileToken:", !turnstileToken, ")");

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <StyledTextField label="Your Name" required value={form.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)} />
      <StyledTextField label="Email" required type="email" value={form.email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)} />
      <StyledTextField label="Phone Number" value={form.phone_number} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('phone_number', e.target.value)} />
      <StyledTextField label="Artist/Band Name" required value={form.artist_name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('artist_name', e.target.value)} />

      <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: '0.9rem' }}>Link to Your Music</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <StyledSelect
          label="Platform"
          value={form.music_link_type}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChange('music_link_type', e.target.value)}
          options={['Spotify', 'Apple Music', 'SoundCloud', 'Bandcamp', 'YouTube', 'Other']}
        />
        <StyledTextField label="Music Link URL" value={form.music_link_url} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('music_link_url', e.target.value)} fullWidth />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <StyledSelect
          label="Country"
          value={form.country}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChange('country', e.target.value)}
          options={countries}
        />
        
        <StyledSelect
          label="Current Distributor"
          value={form.distributor}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChange('distributor', e.target.value)}
          options={[
            'EmuBands', 'DistroKid', 'CD Baby', 'Rotenone', 'SoundOn', 'Amuse', 'Landr', 'TuneCore',
            'The Orchard', 'Believe', 'Fuga', 'Downtown', 'Absolute', 'iGroove', 'Revelator', 'Other'
          ]}
        />
      </Stack>

      <StyledAutocomplete
        multiple
        options={genresList}
        value={form.genres}
        onChange={(_: React.SyntheticEvent<Element, Event>, newValue: string[]) => handleChange('genres', newValue)}
        renderTags={(value: string[], getTagProps: any) =>
          value.map((option: string, index: number) => (
            <Chip 
              variant="outlined" 
              label={option} 
              {...getTagProps({ index })} 
              sx={{ fontFamily: '"Poppins", sans-serif', color: '#000000' }}
            />
          ))
        }
        label="Genre(s)"
      />

      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <StyledFormControlLabel
            control={
              <Checkbox
                checked={form.has_recording_agreement}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('has_recording_agreement', e.target.checked)}
                size="small"
              />
            }
            label="Recording agreement?"
          />
          {form.has_recording_agreement && (
            <StyledTextField
              label="Record Label Name"
              value={form.record_label_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('record_label_name', e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <StyledFormControlLabel
            control={
              <Checkbox
                checked={form.has_publishing_agreement}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('has_publishing_agreement', e.target.checked)}
                size="small"
              />
            }
            label="Publishing agreement?"
          />
          {form.has_publishing_agreement && (
            <StyledTextField
              label="Publisher Name"
              value={form.publisher_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('publisher_name', e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </Stack>

      <Box>
        <StyledFormControlLabel
          control={
            <Checkbox
              checked={form.has_collection_agency}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('has_collection_agency', e.target.checked)}
              size="small"
            />
          }
          label="Are you registered with a collection agency?"
        />
        {form.has_collection_agency && (
          <StyledAutocomplete
            multiple
            options={collectionAgencies}
            value={form.collection_agencies}
            onChange={(_: React.SyntheticEvent<Element, Event>, newValue: string[]) => handleChange('collection_agencies', newValue)}
            renderTags={(value: string[], getTagProps: any) =>
              value.map((option: string, index: number) => (
                <Chip 
                  variant="outlined" 
                  label={option} 
                  {...getTagProps({ index })} 
                  sx={{ fontFamily: '"Poppins", sans-serif', color: '#000000' }}
                />
              ))
            }
            label="Registered Collection Agencies"
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: '0.9rem' }}>Planned Releases (next 12–24 months)</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <StyledSelect
          label="Albums"
          value={form.release_plan_albums === 5 ? '5+' : form.release_plan_albums.toString()}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
            const value = e.target.value as string;
            handleChange('release_plan_albums', value === '5+' ? 5 : parseInt(value))
          }}
          options={['0', '1', '2', '3', '4', '5+']}
        />
        <StyledSelect
          label="EPs"
          value={form.release_plan_eps === 5 ? '5+' : form.release_plan_eps.toString()}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
            const value = e.target.value as string;
            handleChange('release_plan_eps', value === '5+' ? 5 : parseInt(value))
          }}
          options={['0', '1', '2', '3', '4', '5+']}
        />
        <StyledSelect
          label="Singles"
          value={form.release_plan_singles === 5 ? '5+' : form.release_plan_singles.toString()}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
            const value = e.target.value as string;
            handleChange('release_plan_singles', value === '5+' ? 5 : parseInt(value))
          }}
          options={['0', '1', '2', '3', '4', '5+']}
        />
      </Stack>

      <StyledTextField
        label="Why do you want to join Lsnto?"
        multiline
        rows={2}
        value={form.message}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('message', e.target.value)}
      />

      <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: '0.9rem' }}>Social Media Links</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
        <StyledTextField 
          label="Instagram" 
          value={form.social_instagram} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('social_instagram', e.target.value)} 
          fullWidth
        />
        <StyledTextField 
          label="TikTok" 
          value={form.social_tiktok} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('social_tiktok', e.target.value)} 
          fullWidth
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <StyledTextField 
          label="YouTube" 
          value={form.social_youtube} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('social_youtube', e.target.value)} 
          fullWidth
        />
        <StyledTextField 
          label="Other" 
          value={form.social_other} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('social_other', e.target.value)} 
          fullWidth
        />
      </Stack>

      <StyledTextField
        label="Where did you hear about Lsnto?"
        value={form.heard_about}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('heard_about', e.target.value)}
      />

      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
        <StyledFormControlLabel
          control={
            <Checkbox
              checked={form.wants_exclusives}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('wants_exclusives', e.target.checked)}
              size="small"
            />
          }
          label="I'm interested in offering exclusive content"
          sx={{ flex: 1 }}
        />

        <StyledFormControlLabel
          control={
            <Checkbox
              required
              checked={form.owns_rights}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('owns_rights', e.target.checked)}
              size="small"
            />
          }
          label="I confirm I own the rights to my music"
          sx={{ flex: 1 }}
        />
      </Stack>
      
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <div ref={turnstileContainerRef} />
      </Box>

      {error && <Typography color="error" sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.85rem', mt: 1 }}>{error}</Typography>}

      <Button 
        type="submit" 
        variant="contained" 
        disabled={isButtonDisabled}
        sx={{ 
          mt: 2, 
          py: 1, 
          fontFamily: '"Poppins", sans-serif', 
          fontWeight: 600,
          borderRadius: '8px',
          textTransform: 'none',
          fontSize: '0.95rem',
          boxShadow: '0 4px 12px rgba(255, 0, 84, 0.3)',
          background: 'linear-gradient(45deg, #FF0054, #662d91)',
          '&:hover': {
            boxShadow: '0 6px 14px rgba(255, 0, 84, 0.4)',
          }
        }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Join the Waitlist'}
      </Button>
    </Box>
  );
};
console.log("JoinForm RENDER END");

export default JoinForm;